import { useEffect, useState } from 'react';

export interface StageLayout {
  /** 'landscape' only ever applies on a touch layout wider than it is tall
   * (mobile/tablet in landscape); desktop is always 'portrait' (single
   * column, no D-pad). */
  orientation: 'portrait' | 'landscape';
  /** exact CSS-px box available for the maze canvas, already net of the
   * HUD, the D-pad, the gaps between them, AND any safe-area inset (safe
   * area is CSS padding on the stage element itself -- see index.css
   * .game-screen -- explicitly subtracted from the stage's measured
   * padding-box size in recompute() below; BUGLEDGER.md #14).
   *
   * gen-7: on touch/portrait this is now the maze's TRUE letterboxed box
   * (tile * cols x tile * rows), not the full leftover budget -- see
   * DPAD_CHROME/DPAD_MIN_BTN below for why. Desktop/landscape keep the old
   * "full budget" behavior untouched (GameCanvas's own letterbox fit still
   * centers within whatever box it's given either way, so this is purely a
   * tighter box, never a different fit algorithm). */
  canvasW: number;
  canvasH: number;
  /** gen-8: touch-portrait only. The D-pad button's CSS px side length,
   * computed from the SAME real measurements as canvasW/H. Targets a fixed
   * comfortable size (DPAD_PREFERRED_BTN) -- it no longer "grows to fill"
   * whatever vertical slack is left (that was gen-7's behavior, and it's
   * what made the D-pad balloon and sit crowded right under the maze); the
   * D-pad is now pinned to the BOTTOM of the screen via CSS (index.css
   * `.game-body[data-orientation='portrait'] .dpad-dock`), so any real
   * leftover slack shows up as visible gap between the maze and the dock
   * instead. Only shrinks below the preferred size (down to the 44px
   * floor, never below) on a viewport too short to fit maze + preferred
   * D-pad together -- identical fallback math to gen-7's, just with a
   * lower, non-growing target ceiling. Landscape/desktop don't use this
   * (D-pad flanks or is absent) -- always 44 there, matching the CSS
   * fallback. */
  dpadBtn: number;
  ready: boolean;
}

const GAP = 8; // px -- keep in sync with .game-body / .stage-top gap in index.css
const DPAD_MIN_BTN = 44; // px -- SPEC.md §5 touch-target floor, never go below
// px -- comfortable fixed target the D-pad AIMS for, NOT a "grow to fill"
// ceiling. gen-10 brief: raise the default well above the old 60px, into
// the 72-96px comfortable-thumb-target range, and let the Settings
// "control size" preference pick a bigger/smaller target within that same
// range -- see CONTROL_SIZE_PREFERRED_BTN below, threaded in from
// GameScreen.tsx via settings.ts's `controlSize`. Every viewport still
// clamps this DOWN toward (never below) DPAD_MIN_BTN whenever the real
// measured space can't fit the target -- that fit-math (dCeiling below) is
// completely unchanged by this setting.
const DPAD_PREFERRED_BTN = 76;
/** Settings.ts `ControlSize` -> preferred D-pad button px. Exported so
 * GameScreen.tsx (the only caller that knows the live setting) can pass
 * the right one in without this module importing settings.ts itself (kept
 * one-directional: settings.ts has no knowledge of layout math). */
export const CONTROL_SIZE_PREFERRED_BTN: Record<'regular' | 'large' | 'xlarge', number> = {
  regular: 76,
  large: 88,
  // XL "fills to the maze-protecting ceiling": on any phone this exceeds
  // dCeiling (the largest D-pad that keeps the maze width-bound), so dpadBtn
  // clamps to dCeiling -- i.e. as big as the layout allows WITHOUT shrinking
  // the maze. Capped at 140 only so it isn't absurd on very tall devices.
  xlarge: 140,
};
// px -- fixed (non-button-size) chrome inside the D-pad's box: .dpad's two
// 4px row-gaps (8) + .dpad-dock's own bottom padding (8px + safe-area,
// safe-area assumed 0 for this budget -- same simplification GAP above
// already makes; keep in sync with .dpad / .dpad-dock in index.css).
const DPAD_CHROME = 16;

/**
 * Computes the maze canvas's exact pixel box by MEASURING the real
 * rendered size of the stage, the HUD (+ any banners above it) and the
 * D-pad, rather than trusting CSS flexbox auto/stretch sizing to "leave the
 * remainder" -- SPEC.md §5 requires the maze + HUD + D-pad to ALL be
 * visible with zero clipping and zero page scroll at every listed
 * breakpoint in BOTH orientations. Flexbox auto-stretch across a
 * `flex-wrap` line turned out to size the D-pad's own box off the wrong
 * cross-axis reference in landscape (BUGLEDGER.md #4) and left very thin,
 * font-load-sensitive margins in portrait -- a real measurement removes
 * the ambiguity structurally instead of re-tuning breakpoints.
 *
 * Recomputes on:
 *  - a ResizeObserver on the stage element itself (viewport resize,
 *    orientation change, `100dvh` changing as mobile browser chrome
 *    shows/hides),
 *  - a ResizeObserver on the "top stack" (banner + HUD) and the D-pad dock,
 *    so a font swap or a disconnect-banner appearing/disappearing
 *    re-measures instead of leaving a stale box,
 *  - `orientationchange` and `visualViewport` resize, belt-and-suspenders
 *    for engines where the stage's own ResizeObserver callback can lag a
 *    frame behind an orientation flip.
 *
 * Takes the three DOM nodes as plain nullable VALUES (state, not
 * `useRef` objects) -- GameScreen.tsx has an early "Loading..." return
 * (before the first snapshot arrives) whose tree does not mount the stage
 * element at all. A `useRef` object is a stable box whose `.current`
 * mutating from null -> element does NOT itself cause a re-render or
 * re-run any effect, so an effect keyed off e.g. `[touchLayout]` that
 * happens to run while `.current` is still null bails out and then NEVER
 * retries once the real tree (with the node) mounts on a later render --
 * silently leaving `ready: false` forever (this was the actual mechanism
 * behind BUGLEDGER.md #4: the D-pad/HUD/canvas fell back to raw,
 * un-computed CSS sizing -- including the unstyled <canvas> element's
 * browser-default 300x150 intrinsic box -- for the ENTIRE session).
 * Callback refs (`setStageEl` etc., wired to the `ref` prop) turn
 * attach/detach into a real state change, which this hook can depend on
 * directly so it re-subscribes exactly when there is something to
 * observe.
 */
export function useGameLayout(
  stageEl: HTMLElement | null,
  topStackEl: HTMLElement | null,
  dpadEl: HTMLElement | null,
  touchLayout: boolean,
  /** Maze grid size (28x31 today), used ONLY on touch-portrait to compute
   * the true letterboxed box + D-pad growth below -- see StageLayout's own
   * comment. Landscape/desktop ignore these (unchanged "full budget"
   * math), so an undefined/stale value there is harmless. */
  mazeCols?: number,
  mazeRows?: number,
  /** gen-10: the D-pad's preferred (ceiling) button size, from the
   * Settings "control size" preference (CONTROL_SIZE_PREFERRED_BTN above).
   * Defaults to the regular-size target so every OTHER caller (and every
   * existing test) is unaffected. */
  preferredDpadBtn: number = DPAD_PREFERRED_BTN,
): StageLayout {
  const [layout, setLayout] = useState<StageLayout>({
    orientation: 'portrait',
    canvasW: 0,
    canvasH: 0,
    dpadBtn: DPAD_MIN_BTN,
    ready: false,
  });

  useEffect(() => {
    if (!stageEl) return;
    const activeDpadEl = touchLayout ? dpadEl : null;

    function recompute(): void {
      if (!stageEl) return;
      // DEFECT 14 fix: `clientWidth`/`clientHeight` are the element's
      // PADDING box (content + padding, border excluded) -- NOT "content
      // box, already net of padding" as this comment used to claim. That
      // wrong assumption fed the full un-padded box size into the
      // canvas/D-pad budget below, so the computed layout consumed the
      // entire padding box including the safe-area padding itself -- with
      // `overflow: hidden` on .game-screen swallowing the discrepancy
      // silently, the D-pad rendered flush against the true viewport edge
      // (measured gap: 0px) instead of inset by the safe-area padding
      // (BUGLEDGER.md #14). Subtract the real computed padding so `stageW`
      // / `stageH` are the actual usable CONTENT box, and the safe-area
      // inset (index.css .game-screen padding, which bakes in
      // env(safe-area-inset-*) with a non-zero fallback) is genuinely left
      // as margin instead of being silently consumed.
      const stageCs = getComputedStyle(stageEl);
      const padL = parseFloat(stageCs.paddingLeft) || 0;
      const padR = parseFloat(stageCs.paddingRight) || 0;
      const padT = parseFloat(stageCs.paddingTop) || 0;
      const padB = parseFloat(stageCs.paddingBottom) || 0;
      const stageW = stageEl.clientWidth - padL - padR;
      const stageH = stageEl.clientHeight - padT - padB;

      const topH = topStackEl ? topStackEl.getBoundingClientRect().height : 0;
      const dpadRect = activeDpadEl ? activeDpadEl.getBoundingClientRect() : null;
      const dpadW = dpadRect ? dpadRect.width : 0;
      const dpadH = dpadRect ? dpadRect.height : 0;

      const landscape = touchLayout && stageW > stageH;
      let canvasW: number;
      let canvasH: number;
      let dpadBtn = DPAD_MIN_BTN;
      if (landscape) {
        // HUD/banner stack stays full-width on top; D-pad flanks the maze.
        // Unchanged from pre-gen-7 -- D-pad growth is a portrait-only win
        // (real phones hit the rotate gate before reaching this branch;
        // see index.css's rotate-prompt media query).
        canvasW = Math.max(0, stageW - (activeDpadEl ? dpadW + GAP : 0));
        canvasH = Math.max(0, stageH - topH - GAP);
      } else if (touchLayout && activeDpadEl && mazeCols && mazeRows) {
        // gen-8: the maze always gets its TRUE width-bound letterbox size
        // (28 columns cap the tile long before the 31 rows would, on every
        // listed phone breakpoint except the very shortest) -- it is never
        // grown OR shrunk to hand the D-pad more room to fill. The D-pad
        // instead targets a fixed comfortable size (DPAD_PREFERRED_BTN,
        // gen-8 brief: 56-64px) and is pinned to the bottom of the screen
        // via CSS; whatever's left between the maze and the dock is
        // intentional visible slack (SPEC.md gen-8 brief), not something
        // this math needs to eliminate.
        //
        // `bodyBudget` is the old portrait budget MINUS the D-pad
        // reservation term (computed below instead of read off the
        // currently-rendered dpadH, which would still be last render's
        // button size and only converge after an extra ResizeObserver
        // round-trip). `mazeWidthBoundH` is what the maze needs if width
        // alone is binding (the common case on every listed phone
        // breakpoint except the very shortest) -- solving for the D-pad
        // size that would exactly use up all the slack between that height
        // and the full budget gives a single closed-form ceiling on how big
        // the D-pad could get without shrinking the maze:
        //   dpadDockH(D) = 3*D + DPAD_CHROME <= bodyBudget - GAP - mazeWidthBoundH
        // Clamping the PREFERRED size against that ceiling (rather than
        // growing to fill it, gen-7's behavior) means the common case picks
        // the smaller, fixed, non-growing target -- and the narrowest
        // breakpoints where even that ceiling is below 44px still shrink
        // gracefully toward (never below) the 44px floor, falling back to
        // the OLD height-bound letterbox in that one case exactly as gen-7
        // did -- this never overflows regardless of which regime applies.
        const bodyBudget = Math.max(0, stageH - topH - GAP);
        const mazeWidthBoundH = (stageW * mazeRows) / mazeCols;
        const dCeiling = (bodyBudget - GAP - mazeWidthBoundH - DPAD_CHROME) / 3;
        dpadBtn = Math.max(DPAD_MIN_BTN, Math.min(preferredDpadBtn, dCeiling));
        const dpadDockH = 3 * dpadBtn + DPAD_CHROME;
        const remainderH = Math.max(0, bodyBudget - GAP - dpadDockH);
        const tile = Math.max(4, Math.min(stageW / mazeCols, remainderH / mazeRows));
        canvasW = tile * mazeCols;
        canvasH = tile * mazeRows;
      } else {
        canvasW = Math.max(0, stageW);
        canvasH = Math.max(0, stageH - topH - (activeDpadEl ? dpadH + GAP : 0) - GAP);
      }

      setLayout((prev) => {
        const orientation = landscape ? 'landscape' : 'portrait';
        if (
          prev.ready &&
          prev.orientation === orientation &&
          prev.canvasW === canvasW &&
          prev.canvasH === canvasH &&
          prev.dpadBtn === dpadBtn
        ) {
          return prev; // stable size -> no re-render / no GameCanvas resize churn
        }
        return { orientation, canvasW, canvasH, dpadBtn, ready: true };
      });
    }

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(stageEl);
    if (topStackEl) ro.observe(topStackEl);
    if (activeDpadEl) ro.observe(activeDpadEl);
    window.addEventListener('orientationchange', recompute);
    window.visualViewport?.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', recompute);
      window.visualViewport?.removeEventListener('resize', recompute);
    };
  }, [stageEl, topStackEl, dpadEl, touchLayout, mazeCols, mazeRows, preferredDpadBtn]);

  return layout;
}
